import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, LogOut, LayoutDashboard, Menu, X, UserPlus, LogIn } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { cartItemsCount, openCart } = useCart();
  const { user, isAuthenticated, isAdmin, logout } = useAuth();

  // Simple generic user avatar
  const defaultAvatar = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' fill='%23888'%3E%3Ccircle cx='50' cy='50' r='50' fill='%23e8e8e8'/%3E%3Ccircle cx='50' cy='38' r='18' fill='%23aaa'/%3E%3Cpath d='M50 60c-20 0-36 12-36 27v13h72v-13c0-15-16-27-36-27z' fill='%23aaa'/%3E%3C/svg%3E";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const handleLogout = () => {
    logout();
    navigate('/shop');
  };

  const navItems = [
    { name: 'Home', path: '/home' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Policies', path: '/legal' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-[#2e4e18] shadow-lg' : 'bg-[#2e4e18]'
    }`}>
      <div className="container mx-auto px-4 xl:px-8">
        <div className="flex items-center justify-between py-3">
          {/* Logo */}
          <Link to="/shop" className="flex items-center flex-shrink-0">
            <img
              src="/logo.svg"
              alt="Book my fresh"
              className="h-14 w-auto rounded-lg brightness-125 contrast-110"
            />
          </Link>

          {/* Navigation */}
          <nav className="hidden lg:flex items-center space-x-6 xl:space-x-10">
            {navItems.map((item) => (
              <div 
                key={item.name} 
                className="relative"
                onMouseEnter={() => item.dropdown && setOpenDropdown(item.name)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                {item.dropdown ? (
                  <>
                    <button className={`relative flex items-center space-x-1 text-white hover:text-[#FF9800] transition-colors duration-300 font-semibold text-base py-2 whitespace-nowrap ${
                      item.dropdown.some(sub => location.pathname === sub.path) ? 'text-[#FF9800]' : ''
                    }`}>
                      <span>{item.name}</span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === item.name ? 'rotate-180' : ''}`} />
                      {item.dropdown.some(sub => location.pathname === sub.path) && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#FF9800] rounded-full" />
                      )}
                    </button>
                    {openDropdown === item.name && (
                      <div className="absolute top-full left-0 mt-0 w-48 bg-white shadow-xl rounded-lg py-2 z-50">
                        {item.dropdown.map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={subItem.path}
                            className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-[#548B2E] hover:text-white transition-colors duration-200"
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.path}
                    className={`relative text-white hover:text-[#FF9800] transition-colors duration-300 font-semibold text-base py-2 whitespace-nowrap ${
                      location.pathname === item.path ? 'text-[#FF9800]' : ''
                    }`}
                  >
                    {item.name}
                    {location.pathname === item.path && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#FF9800] rounded-full" />
                    )}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Shop Now and Cart/User */}
          <div className="hidden lg:flex items-center gap-3 xl:gap-4">
            {/* Shop Now Button */}
            <Link
              to="/shop"
              className="bg-[#FF9800] hover:bg-[#E88A00] text-white px-5 xl:px-6 py-2 xl:py-2.5 rounded-full font-semibold text-sm xl:text-base transition-all duration-300 hover:shadow-lg whitespace-nowrap"
            >
              Shop Now
            </Link>

            {/* Cart */}
            <button
              onClick={openCart}
              className="relative p-2 text-white hover:text-[#548B2E] transition-colors"
            >
              <ShoppingCart className="w-5 h-5" />
              {cartItemsCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#FF9800] text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center text-[10px]">
                  {cartItemsCount > 9 ? '9+' : cartItemsCount}
                </span>
              )}
            </button>

            {/* User Icon with Hover Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setOpenDropdown('user')}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              {isAuthenticated ? (
                <>
                  <button className="flex items-center gap-2 p-1 rounded-full hover:bg-white/10 transition-colors">
                    <img
                      src={user?.avatar || defaultAvatar}
                      alt={user?.name || 'User'}
                      className="w-9 h-9 rounded-full border-2 border-[#548B2E] object-cover"
                    />
                  </button>

                  {/* Authenticated User Dropdown */}
                  {openDropdown === 'user' && (
                    <div className="absolute right-0 mt-0 w-64 bg-white rounded-xl shadow-2xl py-2 z-50 overflow-hidden">
                      {/* User Info Header */}
                      <div className="px-4 py-3 bg-gradient-to-r from-[#2e4e18] to-[#3f6b20] text-white">
                        <div className="flex items-center gap-3">
                          <img
                            src={user?.avatar || defaultAvatar}
                            alt={user?.name || 'User'}
                            className="w-12 h-12 rounded-full border-2 border-white/30 object-cover"
                          />
                          <div>
                            <p className="font-semibold">{user?.name}</p>
                            <p className="text-xs text-white/70">{user?.email}</p>
                            {isAdmin && (
                              <span className="inline-block mt-1 px-2 py-0.5 bg-[#548B2E] text-white text-xs font-semibold rounded-full">
                                Admin
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Menu Items */}
                      <div className="py-2">
                        {isAdmin && (
                          <Link
                            to="/admin"
                            className="flex items-center gap-3 px-4 py-2.5 text-gray-700 hover:bg-[#548B2E]/10 hover:text-[#548B2E] transition-colors"
                          >
                            <LayoutDashboard className="w-4 h-4" />
                            Admin Dashboard
                          </Link>
                        )}
                        <Link
                          to="/profile"
                          className="flex items-center gap-3 px-4 py-2.5 text-gray-700 hover:bg-[#548B2E]/10 hover:text-[#548B2E] transition-colors"
                        >
                          <User className="w-4 h-4" />
                          My Profile
                        </Link>
                        <Link
                          to="/shop"
                          className="flex items-center gap-3 px-4 py-2.5 text-gray-700 hover:bg-[#548B2E]/10 hover:text-[#548B2E] transition-colors"
                        >
                          <ShoppingCart className="w-4 h-4" />
                          Shop Now
                        </Link>
                      </div>

                      {/* Logout */}
                      <div className="border-t border-gray-100 pt-2">
                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-3 w-full px-4 py-2.5 text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <LogOut className="w-4 h-4" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <button className="p-2 text-white hover:text-[#548B2E] transition-colors rounded-full hover:bg-white/10">
                    <User className="w-6 h-6" />
                  </button>

                  {/* Not Authenticated Dropdown */}
                  {openDropdown === 'user' && (
                    <div className="absolute right-0 mt-0 w-56 bg-white rounded-xl shadow-2xl py-3 z-50 overflow-hidden">
                      <div className="px-4 pb-3 border-b border-gray-100">
                        <p className="text-sm text-gray-600">Welcome to Book my fresh</p>
                        <p className="text-xs text-gray-400">Sign in to access your account</p>
                      </div>
                      <div className="py-2 space-y-1">
                        <Link
                          to="/login"
                          className="flex items-center gap-3 px-4 py-2.5 text-gray-700 hover:bg-[#548B2E]/10 hover:text-[#548B2E] transition-colors"
                        >
                          <LogIn className="w-4 h-4" />
                          Sign In
                        </Link>
                        <Link
                          to="/register"
                          className="flex items-center gap-3 px-4 py-2.5 text-gray-700 hover:bg-[#548B2E]/10 hover:text-[#548B2E] transition-colors"
                        >
                          <UserPlus className="w-4 h-4" />
                          Create Account
                        </Link>
                      </div>
                      <div className="px-4 pt-2 border-t border-gray-100">
                        <Link
                          to="/shop"
                          className="block w-full text-center bg-[#FF9800] hover:bg-[#F57C00] text-white py-2 rounded-lg font-semibold text-sm transition-colors"
                        >
                          Shop Now
                        </Link>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-3">
            <button
              onClick={openCart}
              className="relative p-2 text-white hover:text-[#548B2E] transition-colors"
            >
              <ShoppingCart className="w-5 h-5" />
              {cartItemsCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#FF9800] text-white text-xs font-bold w-4 h-4 rounded-full flex items-center justify-center text-[10px]">
                  {cartItemsCount > 9 ? '9+' : cartItemsCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-white hover:text-[#548B2E] transition-colors"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-[#2e4e18] border-t border-white/10 py-4">
            {/* Mobile Shop Now Button */}
            <div className="px-4 mb-4">
              <Link
                to="/shop"
                className="block w-full text-center bg-[#FF9800] hover:bg-[#E88A00] text-white px-6 py-3 rounded-full font-semibold transition-all duration-300"
              >
                Shop Now
              </Link>
            </div>
            
            <nav className="flex flex-col space-y-2">
              {navItems.map((item) => (
                <div key={item.name}>
                  {item.dropdown ? (
                    <>
                      <div className="px-4 py-2 text-white font-semibold text-base">{item.name}</div>
                      {item.dropdown.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.path}
                          className="block px-8 py-2 text-white/70 hover:text-[#548B2E] transition-colors"
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </>
                  ) : (
                    <Link
                      to={item.path}
                      className={`block px-4 py-2 text-white hover:text-[#548B2E] transition-colors font-semibold text-base ${
                        location.pathname === item.path ? 'text-[#548B2E]' : ''
                      }`}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
              
              <div className="border-t border-white/10 mt-2 pt-4 px-4 space-y-3">
                {isAuthenticated ? (
                  <>
                    <div className="flex items-center justify-between bg-white/10 rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <img
                          src={user?.avatar || defaultAvatar}
                          alt={user?.name || 'User'}
                          className="w-10 h-10 rounded-full border-2 border-[#548B2E]"
                        />
                        <div>
                          <p className="text-white font-medium text-sm">{user?.name}</p>
                          <p className="text-white/60 text-xs">{isAdmin ? 'Admin' : 'Member'}</p>
                        </div>
                      </div>
                      <button
                        onClick={handleLogout}
                        className="p-2 text-white/70 hover:text-red-400 transition-colors"
                      >
                        <LogOut className="w-5 h-5" />
                      </button>
                    </div>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        className="block w-full text-center bg-[#548B2E] text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-all hover:bg-[#3f6b20]"
                      >
                        Admin Dashboard
                      </Link>
                    )}
                    <Link
                      to="/profile"
                      className="block w-full text-center border border-white/30 text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-all hover:bg-white/10"
                    >
                      My Profile
                    </Link>
                  </>
                ) : (
                  <div className="flex gap-2">
                    <Link
                      to="/login"
                      className="flex-1 text-center text-white border border-white/30 px-4 py-2.5 rounded-full font-medium text-sm hover:bg-white/10 transition-all"
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/register"
                      className="flex-1 text-center bg-[#548B2E] text-white px-4 py-2.5 rounded-full font-medium text-sm hover:bg-[#3f6b20] transition-all"
                    >
                      Register
                    </Link>
                  </div>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
